import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, switchMap, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { AuthService } from "./auth.service";
import { ToastService } from "./toast.service";
import { MechanicsService } from "./mechanics.service";

@Injectable({
  providedIn: "root",
})
export class UserService {
  constructor(
    private http: HttpClient,
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService,
    private ms: MechanicsService
  ) {}

  /**
   * Sync Cognito user data
   * This method calls the cognitoSync endpoint to check if MFA registration is required
   * Returns an observable that emits the response or throws an error with the error details
   */
  cognitoSync(): Observable<any> {
    return this.http
      .put<any>(
        `${environment.backendUrl}/cognitoSync`,
        {},
        { observe: "response" }
      )
      .pipe(
        switchMap((response) => {
          // If status is 200, return the response body
          if (response.status === 200) {
            return of(response.body || { success: true });
          }
          // For other success status codes, still return success
          return of(response.body || { success: true });
        }),
        catchError((error) => {
          // Don't use handleError here as we want to check the error code in the calling component
          // Return the error so the caller can check for specific error codes
          return throwError(() => error);
        })
      );
  }

  /**
   * Sync Cognito user data with automatic error handling
   * This method calls cognitoSync and handles errors automatically:
   * - 400 with "invalid_client": Logs out the user
   * - 403 with "WIPO-CUS-16103": Navigates to MFA registration
   * - 401: Shows error message and navigates to unauthorized page
   * Returns an observable that emits success or handles errors internally
   */
  cognitoSyncWithErrorHandling(): Observable<any> {
    return this.cognitoSync().pipe(
      catchError((error: any) => {
        const errorResponse = error?.error;
        const status = error?.status;
        const errorCode = errorResponse?.wipoErrorCode?.code;
        const errorMessage =
          errorResponse?.message ||
          error?.message ||
          "An unexpected error occurred";

        // Handle 400 with "invalid_client" - Logout user
        // Error format: "Client error : invalid_client"
        if (
          status === 400 &&
          errorMessage?.toLowerCase().includes("invalid_client")
        ) {
          console.error("Invalid client error, logging out user");
          this.authService.logout().subscribe({
            next: () => {
              this.router.navigate(["/default/en/sign-in"]);
            },
            error: () => {
              this.router.navigate(["/default/en/sign-in"]);
            },
          });
          return throwError(
            () => new Error("Invalid client - user logged out")
          );
        }

        // Handle 403 with "WIPO-CUS-16103" - Navigate to MFA registration
        if (status === 403 && errorCode === "WIPO-CUS-16103") {
          console.log(
            "MFA registration required, navigating to MFA registration"
          );
          this.router.navigate(["/mfa-registration"]);
          return throwError(() => new Error("MFA registration required"));
        }

        // Handle 401 - Show error and navigate to unauthorized page
        if (status === 401) {
          const officeCode = this.ms.getCurrentOffice() || "default";
          const langCode = this.ms.getDefaultLanguage() || "en";
          this.toastService.showError(
            "Unauthorized",
            errorMessage || "You are not authorized to access this resource."
          );
          this.router.navigate([`/${officeCode}/${langCode}/unauthorized`]);
          return throwError(() => new Error("Unauthorized"));
        }

        // Handle 503 - Service Unavailable
        if (status === 503) {
          this.toastService.showError(
            "Service Unavailable",
            errorMessage ||
              "The service is temporarily unavailable. Please try again later."
          );
          // Preserve the original error with status code for component handling
          const serviceUnavailableError: any = new Error("Service Unavailable");
          serviceUnavailableError.status = 503;
          serviceUnavailableError.error = errorResponse;
          return throwError(() => serviceUnavailableError);
        }

        // For other errors, re-throw to let the caller handle
        return throwError(() => error);
      })
    );
  }
}
